import { CurrencyLimitData } from '../typings/constants';
import { CampaignId, CampaignPlatform } from '../typings/domain';
import { GdCampaignBudgetShowMode } from '../typings/graphql-grid';
import {
    AttributionModel,
    DnaStrategyType,
    OptimizationCriteria,
    Strategy,
    StrategyEditorInitData,
    StrategyEditorValidationResult,
    StrategyForEditor
} from '../typings/strategy';

export enum StrategyEditorActionType {
    OPEN = 'StrategyEditorAction/OPEN',
    CLOSE = 'StrategyEditorAction/CLOSE',
    INIT = 'StrategyEditorAction/INIT',
    CHANGE_TYPE = 'StrategyEditorAction/CHANGE_TYPE',
    CHANGE_PLATFORM = 'StrategyEditorAction/CHANGE_PLATFORM',
    CHANGE_ATTRIBUTION_MODEL = 'StrategyEditorAction/CHANGE_ATTRIBUTION_MODEL',
    TOGGLE_AVG_CLICK_PRICE_ENABLED = 'StrategyEditorAction/TOGGLE_AVG_CLICK_PRICE_ENABLED',
    CHANGE_AVG_CLICK_PRICE = 'StrategyEditorAction/CHANGE_AVG_CLICK_PRICE',
    BLUR_AVG_CLICK_PRICE = 'StrategyEditorAction/BLUR_AVG_CLICK_PRICE',
    CHANGE_WEEK_BUNDLE = 'StrategyEditorAction/CHANGE_WEEK_BUNDLE',
    BLUR_WEEK_BUNDLE = 'StrategyEditorAction/BLUR_WEEK_BUNDLE',
    CHANGE_AVG_CPA_PRICE = 'StrategyEditorAction/CHANGE_AVG_CPA_PRICE',
    BLUR_AVG_CPA_PRICE = 'StrategyEditorAction/BLUR_AVG_CPA_PRICE',
    CHANGE_CONTEXT_LIMIT = 'StrategyEditorAction/CHANGE_CONTEXT_LIMIT',
    CHANGE_CPC_HOLD = 'StrategyEditorAction/CHANGE_CPC_HOLD',
    CHANGE_DAILY_BUDGET_MODE = 'StrategyEditorAction/CHANGE_DAILY_BUDGET_MODE',
    CHANGE_DAILY_BUDGET = 'StrategyEditorAction/CHANGE_DAILY_BUDGET',
    BLUR_DAILY_BUDGET = 'StrategyEditorAction/BLUR_DAILY_BUDGET',
    CHANGE_GOAL_ID = 'StrategyEditorAction/CHANGE_GOAL_ID',
    CHANGE_MAX_CLICK_PRICE = 'StrategyEditorAction/CHANGE_MAX_CLICK_PRICE',
    BLUR_MAX_CLICK_PRICE = 'StrategyEditorAction/BLUR_MAX_CLICK_PRICE',
    CHANGE_PROFITABILITY = 'StrategyEditorAction/CHANGE_PROFITABILITY',
    BLUR_PROFITABILITY = 'StrategyEditorAction/BLUR_PROFITABILITY',
    CHANGE_RESERVE_RETURN = 'StrategyEditorAction/CHANGE_RESERVE_RETURN',
    CHANGE_ROI_COEF = 'StrategyEditorAction/CHANGE_ROI_COEF',
    BLUR_ROI_COEF = 'StrategyEditorAction/BLUR_ROI_COEF',
    CHANGE_SEPARATE_BIDDING = 'StrategyEditorAction/CHANGE_SEPARATE_BIDDING',
    TOGGLE_WEEKLY_BUDGET_ENABLED = 'StrategyEditorAction/TOGGLE_WEEKLY_BUDGET_ENABLED',
    CHANGE_WEEKLY_BUDGET = 'StrategyEditorAction/CHANGE_WEEKLY_BUDGET',
    BLUR_WEEKLY_BUDGET = 'StrategyEditorAction/BLUR_WEEKLY_BUDGET',
    TOGGLE_OPTIMIZATION_PARAMETERS = 'StrategyEditorAction/TOGGLE_OPTIMIZATION_PARAMETERS',
    TOGGLE_DAILY_BUDGET_PARAMETERS = 'StrategyEditorAction/TOGGLE_DAILY_BUDGET_PARAMETERS',
    TOGGLE_AUXILIARY_PARAMETERS = 'StrategyEditorAction/TOGGLE_AUXILIARY_PARAMETERS',
    VALIDATE_AND_ATTEMPT_SAVE = 'StrategyEditorAction/VALIDATE_AND_ATTEMPT_SAVE',
    APPLY_VALIDATION_RESULT = 'StrategyEditorAction/APPLY_VALIDATION_RESULT',
    REVERT_EDITING = 'StrategyEditorAction/REVERT_EDITING',
    CHANGE_OPTIMIZATION_CRITERIA = 'StrategyEditorAction/CHANGE_OPTIMIZATION_CRITERIA'
}

export interface OpenStrategyEditorAction {
    type: StrategyEditorActionType.OPEN;
    editorId: string;
}

export function openStrategyEditor(editorId: string): OpenStrategyEditorAction {
    return {
        type: StrategyEditorActionType.OPEN,
        editorId
    };
}

export interface CloseStrategyEditorAction {
    type: StrategyEditorActionType.CLOSE;
    editorId: string;
}

export function closeStrategyEditor(
    editorId: string
): CloseStrategyEditorAction {
    return {
        type: StrategyEditorActionType.CLOSE,
        editorId
    };
}

export interface InitStrategyEditorsAction {
    type: StrategyEditorActionType.INIT;
    payload: {
        editorId: string;
        strategy: StrategyForEditor;
    }[];
}

export function initStrategyEditors(
    payload: StrategyEditorInitData[]
): InitStrategyEditorsAction {
    return {
        type: StrategyEditorActionType.INIT,
        payload
    };
}

export interface ChangeStrategyTypeAction {
    type: StrategyEditorActionType.CHANGE_TYPE;
    editorId: string;
    strategyType: DnaStrategyType;
}

export function changeStrategyType(
    editorId: string,
    strategyType: DnaStrategyType
): ChangeStrategyTypeAction {
    return {
        type: StrategyEditorActionType.CHANGE_TYPE,
        editorId,
        strategyType
    };
}

export interface ChangeStrategyPlatformAction {
    type: StrategyEditorActionType.CHANGE_PLATFORM;
    editorId: string;
    platform: CampaignPlatform;
}

export function changeStrategyPlatform(
    editorId: string,
    platform: CampaignPlatform
): ChangeStrategyPlatformAction {
    return {
        type: StrategyEditorActionType.CHANGE_PLATFORM,
        editorId,
        platform
    };
}

export interface ChangeStrategyAttributionModelAction {
    type: StrategyEditorActionType.CHANGE_ATTRIBUTION_MODEL;
    editorId: string;
    attributionModel: AttributionModel;
}

export function changeStrategyAttributionModel(
    editorId: string,
    attributionModel: AttributionModel
): ChangeStrategyAttributionModelAction {
    return {
        type: StrategyEditorActionType.CHANGE_ATTRIBUTION_MODEL,
        editorId,
        attributionModel
    };
}

export interface ChangeStrategyAvgClickPriceAction {
    type: StrategyEditorActionType.CHANGE_AVG_CLICK_PRICE;
    editorId: string;
    avgClickPrice: string;
}

export function changeStrategyAvgClickPrice(
    editorId: string,
    avgClickPrice: string
): ChangeStrategyAvgClickPriceAction {
    return {
        type: StrategyEditorActionType.CHANGE_AVG_CLICK_PRICE,
        editorId,
        avgClickPrice
    };
}

export interface BlurStrategyAvgClickPriceAction {
    type: StrategyEditorActionType.BLUR_AVG_CLICK_PRICE;
    editorId: string;
    constants?: CurrencyLimitData;
}

export function blurStrategyAvgClickPrice(
    editorId: string,
    constants?: CurrencyLimitData
): BlurStrategyAvgClickPriceAction {
    return {
        type: StrategyEditorActionType.BLUR_AVG_CLICK_PRICE,
        editorId,
        constants
    };
}

export interface ChangeStrategyAvgCpaPriceAction {
    type: StrategyEditorActionType.CHANGE_AVG_CPA_PRICE;
    editorId: string;
    avgCpaPrice: string;
}

export function changeStrategyAvgCpaPrice(
    editorId: string,
    avgCpaPrice: string
): ChangeStrategyAvgCpaPriceAction {
    return {
        type: StrategyEditorActionType.CHANGE_AVG_CPA_PRICE,
        editorId,
        avgCpaPrice
    };
}

export interface BlurStrategyAvgCpaPriceAction {
    type: StrategyEditorActionType.BLUR_AVG_CPA_PRICE;
    editorId: string;
    constants?: CurrencyLimitData;
}

export function blurStrategyAvgCpaPrice(
    editorId: string,
    constants?: CurrencyLimitData
): BlurStrategyAvgCpaPriceAction {
    return {
        type: StrategyEditorActionType.BLUR_AVG_CPA_PRICE,
        editorId,
        constants
    };
}

export interface ChangeStrategyContextLimitAction {
    type: StrategyEditorActionType.CHANGE_CONTEXT_LIMIT;
    editorId: string;
    contextLimit: number;
}

export function changeStrategyContextLimit(
    editorId: string,
    contextLimit: number
): ChangeStrategyContextLimitAction {
    return {
        type: StrategyEditorActionType.CHANGE_CONTEXT_LIMIT,
        editorId,
        contextLimit
    };
}

export interface ChangeStrategyDailyBudgetModeAction {
    type: StrategyEditorActionType.CHANGE_DAILY_BUDGET_MODE;
    editorId: string;
    dailyBudgetMode: GdCampaignBudgetShowMode;
}

export function changeStrategyDailyBudgetMode(
    editorId: string,
    dailyBudgetMode: GdCampaignBudgetShowMode
): ChangeStrategyDailyBudgetModeAction {
    return {
        type: StrategyEditorActionType.CHANGE_DAILY_BUDGET_MODE,
        editorId,
        dailyBudgetMode
    };
}

export interface ChangeStrategyDailyBudgetAction {
    type: StrategyEditorActionType.CHANGE_DAILY_BUDGET;
    editorId: string;
    dailyBudget: string;
}

export function changeStrategyDailyBudget(
    editorId: string,
    dailyBudget: string
): ChangeStrategyDailyBudgetAction {
    return {
        type: StrategyEditorActionType.CHANGE_DAILY_BUDGET,
        editorId,
        dailyBudget
    };
}

export interface BlurStrategyDailyBudgetAction {
    type: StrategyEditorActionType.BLUR_DAILY_BUDGET;
    editorId: string;
    constants?: CurrencyLimitData;
}

export function blurStrategyDailyBudget(
    editorId: string,
    constants?: CurrencyLimitData
): BlurStrategyDailyBudgetAction {
    return {
        type: StrategyEditorActionType.BLUR_DAILY_BUDGET,
        editorId,
        constants
    };
}

export interface ChangeStrategyGoalIdAction {
    type: StrategyEditorActionType.CHANGE_GOAL_ID;
    editorId: string;
    goalId: string;
}

export function changeStrategyGoalId(
    editorId: string,
    goalId: string
): ChangeStrategyGoalIdAction {
    return {
        type: StrategyEditorActionType.CHANGE_GOAL_ID,
        editorId,
        goalId
    };
}

export interface ChangeStrategyMaxClickPriceAction {
    type: StrategyEditorActionType.CHANGE_MAX_CLICK_PRICE;
    editorId: string;
    maxClickPrice: string;
}

export function changeStrategyMaxClickPrice(
    editorId: string,
    maxClickPrice: string
): ChangeStrategyMaxClickPriceAction {
    return {
        type: StrategyEditorActionType.CHANGE_MAX_CLICK_PRICE,
        editorId,
        maxClickPrice
    };
}

export interface BlurStrategyMaxClickPriceAction {
    type: StrategyEditorActionType.BLUR_MAX_CLICK_PRICE;
    editorId: string;
    constants?: CurrencyLimitData;
}

export function blurStrategyMaxClickPrice(
    editorId: string,
    constants?: CurrencyLimitData
): BlurStrategyMaxClickPriceAction {
    return {
        type: StrategyEditorActionType.BLUR_MAX_CLICK_PRICE,
        editorId,
        constants
    };
}

export interface ChangeStrategyProfitabilityAction {
    type: StrategyEditorActionType.CHANGE_PROFITABILITY;
    editorId: string;
    profitability: string;
}

export function changeStrategyProfitability(
    editorId: string,
    profitability: string
): ChangeStrategyProfitabilityAction {
    return {
        type: StrategyEditorActionType.CHANGE_PROFITABILITY,
        editorId,
        profitability
    };
}

export interface BlurStrategyProfitabilityAction {
    type: StrategyEditorActionType.BLUR_PROFITABILITY;
    editorId: string;
    constants?: CurrencyLimitData;
}

export function blurStrategyProfitability(
    editorId: string,
    constants?: CurrencyLimitData
): BlurStrategyProfitabilityAction {
    return {
        type: StrategyEditorActionType.BLUR_PROFITABILITY,
        editorId,
        constants
    };
}

export interface ChangeStrategyReserveReturnAction {
    type: StrategyEditorActionType.CHANGE_RESERVE_RETURN;
    editorId: string;
    reserveReturn: number;
}

export function changeStrategyReserveReturn(
    editorId: string,
    reserveReturn: number
): ChangeStrategyReserveReturnAction {
    return {
        type: StrategyEditorActionType.CHANGE_RESERVE_RETURN,
        editorId,
        reserveReturn
    };
}

export interface ChangeStrategyRoiCoefAction {
    type: StrategyEditorActionType.CHANGE_ROI_COEF;
    editorId: string;
    roiCoef: string;
}

export function changeStrategyRoiCoef(
    editorId: string,
    roiCoef: string
): ChangeStrategyRoiCoefAction {
    return {
        type: StrategyEditorActionType.CHANGE_ROI_COEF,
        editorId,
        roiCoef
    };
}

export interface BlurStrategyRoiCoefAction {
    type: StrategyEditorActionType.BLUR_ROI_COEF;
    editorId: string;
    constants?: CurrencyLimitData;
}

export function blurStrategyRoiCoef(
    editorId: string,
    constants?: CurrencyLimitData
): BlurStrategyRoiCoefAction {
    return {
        type: StrategyEditorActionType.BLUR_ROI_COEF,
        editorId,
        constants
    };
}

export interface ChangeStrategySeparateBiddingAction {
    type: StrategyEditorActionType.CHANGE_SEPARATE_BIDDING;
    editorId: string;
}

export function changeStrategySeparateBidding(
    editorId: string
): ChangeStrategySeparateBiddingAction {
    return {
        type: StrategyEditorActionType.CHANGE_SEPARATE_BIDDING,
        editorId
    };
}

export interface ChangeStrategyWeeklyBudgetAction {
    type: StrategyEditorActionType.CHANGE_WEEKLY_BUDGET;
    editorId: string;
    weeklyBudget: string;
}

export function changeStrategyWeeklyBudget(
    editorId: string,
    weeklyBudget: string
): ChangeStrategyWeeklyBudgetAction {
    return {
        type: StrategyEditorActionType.CHANGE_WEEKLY_BUDGET,
        editorId,
        weeklyBudget
    };
}

export interface BlurStrategyWeeklyBudgetAction {
    type: StrategyEditorActionType.BLUR_WEEKLY_BUDGET;
    editorId: string;
    constants?: CurrencyLimitData;
}

export function blurStrategyWeeklyBudget(
    editorId: string,
    constants?: CurrencyLimitData
): BlurStrategyWeeklyBudgetAction {
    return {
        type: StrategyEditorActionType.BLUR_WEEKLY_BUDGET,
        editorId,
        constants
    };
}

export interface ToggleOptimizationParametersAction {
    type: StrategyEditorActionType.TOGGLE_OPTIMIZATION_PARAMETERS;
    editorId: string;
}

export function toggleOptimizationParameters(
    editorId: string
): ToggleOptimizationParametersAction {
    return {
        type: StrategyEditorActionType.TOGGLE_OPTIMIZATION_PARAMETERS,
        editorId
    };
}

export interface ToggleDailyBudgetParametersAction {
    type: StrategyEditorActionType.TOGGLE_DAILY_BUDGET_PARAMETERS;
    editorId: string;
}

export function toggleDailyBudgetParameters(
    editorId: string
): ToggleDailyBudgetParametersAction {
    return {
        type: StrategyEditorActionType.TOGGLE_DAILY_BUDGET_PARAMETERS,
        editorId
    };
}

export interface ToggleAuxiliaryParametersAction {
    type: StrategyEditorActionType.TOGGLE_AUXILIARY_PARAMETERS;
    editorId: string;
}

export function toggleAuxiliaryParameters(
    editorId: string
): ToggleAuxiliaryParametersAction {
    return {
        type: StrategyEditorActionType.TOGGLE_AUXILIARY_PARAMETERS,
        editorId
    };
}

export interface ValidateAndAttemptSaveAction {
    type: StrategyEditorActionType.VALIDATE_AND_ATTEMPT_SAVE;
    campaignId: CampaignId;
    strategy: StrategyForEditor;
}

export function validateAndAttemptSave(
    campaignId: CampaignId,
    strategy: StrategyForEditor
): ValidateAndAttemptSaveAction {
    return {
        type: StrategyEditorActionType.VALIDATE_AND_ATTEMPT_SAVE,
        campaignId,
        strategy
    };
}

export interface ChangeCpcHoldAction {
    type: StrategyEditorActionType.CHANGE_CPC_HOLD;
    editorId: string;
}

export function changeCpcHold(editorId: string): ChangeCpcHoldAction {
    return {
        type: StrategyEditorActionType.CHANGE_CPC_HOLD,
        editorId
    };
}

export interface ApplyStrategyValidationResultAction {
    type: StrategyEditorActionType.APPLY_VALIDATION_RESULT;
    editorId: string;
    validationResult: StrategyEditorValidationResult;
}

export function applyStrategyEditorValidationResult(
    editorId: string,
    validationResult: StrategyEditorValidationResult
): ApplyStrategyValidationResultAction {
    return {
        type: StrategyEditorActionType.APPLY_VALIDATION_RESULT,
        editorId,
        validationResult
    };
}

export interface RevertStrategyEditorAction {
    type: StrategyEditorActionType.REVERT_EDITING;
    editorId: string;
    strategy: Strategy;
}

export function revertStrategyEditor(
    editorId: string,
    strategy: Strategy
): RevertStrategyEditorAction {
    return {
        type: StrategyEditorActionType.REVERT_EDITING,
        editorId,
        strategy
    };
}

export interface ChangeOptimizationCriteriaAction {
    type: StrategyEditorActionType.CHANGE_OPTIMIZATION_CRITERIA;
    editorId: string;
    optimizationCriteria: OptimizationCriteria;
}

export function changeOptimizationCriteria(
    editorId: string,
    optimizationCriteria: OptimizationCriteria
): ChangeOptimizationCriteriaAction {
    return {
        type: StrategyEditorActionType.CHANGE_OPTIMIZATION_CRITERIA,
        editorId,
        optimizationCriteria
    };
}

export interface ToggleAvgClickPriceEnabledAction {
    type: StrategyEditorActionType.TOGGLE_AVG_CLICK_PRICE_ENABLED;
    editorId: string;
}

export function toggleAvgClickPriceEnabled(
    editorId: string
): ToggleAvgClickPriceEnabledAction {
    return {
        type: StrategyEditorActionType.TOGGLE_AVG_CLICK_PRICE_ENABLED,
        editorId
    };
}

export interface ToggleWeeklyBudgetEnabledAction {
    type: StrategyEditorActionType.TOGGLE_WEEKLY_BUDGET_ENABLED;
    editorId: string;
}

export function toggleWeeklyBudgetEnabled(
    editorId: string
): ToggleWeeklyBudgetEnabledAction {
    return {
        type: StrategyEditorActionType.TOGGLE_WEEKLY_BUDGET_ENABLED,
        editorId
    };
}

export interface ChangeStrategyWeekBundleAction {
    type: StrategyEditorActionType.CHANGE_WEEK_BUNDLE;
    editorId: string;
    weekBundle: string;
}

export function changeStrategyWeekBundle(
    editorId: string,
    weekBundle: string
): ChangeStrategyWeekBundleAction {
    return {
        type: StrategyEditorActionType.CHANGE_WEEK_BUNDLE,
        editorId,
        weekBundle
    };
}

export interface BlurStrategyWeekBundleAction {
    type: StrategyEditorActionType.BLUR_WEEK_BUNDLE;
    editorId: string;
    constants?: CurrencyLimitData;
}

export function blurStrategyWeekBundle(
    editorId: string,
    constants?: CurrencyLimitData
): BlurStrategyWeekBundleAction {
    return {
        type: StrategyEditorActionType.BLUR_WEEK_BUNDLE,
        editorId,
        constants
    };
}

export type StrategyEditorAction =
    | InitStrategyEditorsAction
    | OpenStrategyEditorAction
    | CloseStrategyEditorAction
    | ChangeStrategyTypeAction
    | ChangeStrategyPlatformAction
    | ChangeStrategyAttributionModelAction
    | ChangeStrategyAvgClickPriceAction
    | ChangeStrategyAvgCpaPriceAction
    | ChangeStrategyContextLimitAction
    | ChangeStrategyDailyBudgetModeAction
    | ChangeStrategyDailyBudgetAction
    | ChangeStrategyGoalIdAction
    | ChangeStrategyMaxClickPriceAction
    | ChangeStrategyProfitabilityAction
    | ChangeStrategyReserveReturnAction
    | ChangeStrategyRoiCoefAction
    | ChangeStrategySeparateBiddingAction
    | ChangeStrategyWeeklyBudgetAction
    | ToggleOptimizationParametersAction
    | ToggleDailyBudgetParametersAction
    | ToggleAuxiliaryParametersAction
    | ValidateAndAttemptSaveAction
    | ChangeCpcHoldAction
    | ApplyStrategyValidationResultAction
    | RevertStrategyEditorAction
    | ChangeOptimizationCriteriaAction
    | ToggleAvgClickPriceEnabledAction
    | ToggleWeeklyBudgetEnabledAction
    | ChangeStrategyWeekBundleAction
    | BlurStrategyAvgClickPriceAction
    | BlurStrategyWeekBundleAction
    | BlurStrategyWeeklyBudgetAction
    | BlurStrategyAvgCpaPriceAction
    | BlurStrategyRoiCoefAction
    | BlurStrategyDailyBudgetAction
    | BlurStrategyMaxClickPriceAction
    | BlurStrategyProfitabilityAction;
